import React, { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import styled from 'styled-components'
import ReactMarkdown from 'react-markdown'
import { FormattedMessage } from 'gatsby-plugin-intl'
import { breakpoint } from 'src/theme/grid'
import OfferDetail from 'src/features/marketplace/components/OfferDetail'
import CircularProgress from '@material-ui/core/CircularProgress'
import MarketplaceService from 'src/services/MarketplaceService'
import { MARKETPLACEOFFER_TRACKING_EVENT_TYPES } from 'src/ducks/consts'
import useOfferTracking from 'src/features/marketplace/hooks/useMarketplaceOfferTracking'
import { navigate, useLocation } from '@reach/router'

const Loader = styled(CircularProgress)`
  width: 25px !important;
  height: 25px !important;
  color: ${({ theme }) => theme.colors.apple.hex()} !important;
`

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  padding: 2rem;
  border-radius: 4px;
  min-width: 200px;
  background: transparent;
`

const OfferImageContainer = styled.div`
  width: 100%;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding-top: 40%;
  overflow: hidden;
`

const OfferImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

const LogoContainer = styled.div`
  width: 100%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding-top: 40%;
  font-size: 0;

  img {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 85%;
    max-height: 85%;
    object-fit: contain;
    padding: 0.5rem;
  }
`

const OfferCard = ({ offer, onClick }) => {
  const { trackEvent } = useOfferTracking()
  const description = offer.description || ''
  let title = offer.title || ''
  if (offer.isDraft) {
    title = title ? `-DRAFT- ${title}` : '-DRAFT-'
  }
  const offerHook = offer.offerHook || ''

  const getImageSource = () => {
    if (offer.cardImage?.url) {
      return offer.cardImage.url
    }

    if (offer.logo?.url) {
      return offer.logo.url
    }

    return null
  }

  const imageSource = getImageSource()
  const isLogoOnly = !offer.cardImage?.url && offer.logo?.url

  const handleClick = async () => {
    try {
      await trackEvent(
        offer.id,
        MARKETPLACEOFFER_TRACKING_EVENT_TYPES.OFFER_VIEWED,
        () => onClick(offer)
      )
    } catch (error) {
      onClick(offer)
    }
  }

  return (
    <CardWrapper onClick={handleClick}>
      {imageSource && !isLogoOnly ? (
        <OfferImageContainer>
          <OfferImage src={imageSource} alt={title} />
        </OfferImageContainer>
      ) : (
        <LogoContainer>
          {offer.logo?.url && (
            <img
              src={offer.logo.url}
              alt={title}
              onError={e => {
                e.target.style.display = 'none'
              }}
            />
          )}
        </LogoContainer>
      )}
      <CardContent>
        {offer.offerHook && <DiscountTag>{offerHook}</DiscountTag>}
        <CardTitle>
          <ReactMarkdown>{title}</ReactMarkdown>
        </CardTitle>
        <CardDescription>
          <ReactMarkdown>{description}</ReactMarkdown>
        </CardDescription>
      </CardContent>
    </CardWrapper>
  )
}

const OFFER_QUERY_PARAM = 'offerId'

const OffersList = () => {
  const location = useLocation()
  const [selectedOfferId, setSelectedOfferId] = useState(() => {
    const params = new URLSearchParams(location.search)
    return params.get(OFFER_QUERY_PARAM)
  })

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const offerIdFromUrl = params.get(OFFER_QUERY_PARAM)

    setSelectedOfferId(currentId => {
      if (offerIdFromUrl && offerIdFromUrl !== currentId) {
        return offerIdFromUrl
      }

      if (!offerIdFromUrl && currentId) {
        return null
      }

      return currentId
    })
  }, [location.search])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const currentOfferId = params.get(OFFER_QUERY_PARAM)
    const nextOfferId = selectedOfferId || null

    if (nextOfferId) {
      if (currentOfferId !== nextOfferId) {
        params.set(OFFER_QUERY_PARAM, nextOfferId)
        const searchString = params.toString()
        const targetUrl = `${
          location.pathname
        }?${searchString}${location.hash || ''}`
        navigate(targetUrl, { replace: true })
      }
    } else if (currentOfferId) {
      params.delete(OFFER_QUERY_PARAM)
      const searchString = params.toString()
      const targetUrl = `${location.pathname}${
        searchString ? `?${searchString}` : ''
      }${location.hash || ''}`
      navigate(targetUrl, { replace: true })
    }
  }, [selectedOfferId, location.pathname, location.search, location.hash])

  const offersQuery = useQuery({
    queryKey: ['marketplace-offers'],
    queryFn: MarketplaceService.showOffers,
    refetchOnWindowFocus: false
  })

  const offerQuery = useQuery({
    queryKey: ['marketplace-offers', selectedOfferId],
    queryFn: () =>
      selectedOfferId ? MarketplaceService.showOffer(selectedOfferId) : null,
    enabled: !!selectedOfferId,
    refetchOnWindowFocus: false
  })

  const handleOfferClick = offer => {
    const nextId = String(offer.id)

    if (nextId === selectedOfferId) {
      return
    }

    setSelectedOfferId(nextId)
  }

  const handleCloseDetail = () => {
    setSelectedOfferId(null)
  }

  if (offersQuery.isLoading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    )
  }

  if (offersQuery.isError) {
    return (
      <EmptyOffersContainer>
        <div>Error: {offersQuery.error?.message}</div>
      </EmptyOffersContainer>
    )
  }

  // Check whether any offers are available.
  if (!offersQuery.data?.data || offersQuery.data.data.length === 0) {
    return (
      <EmptyOffersContainer>
        <FormattedMessage id='offers.noOffers' />
      </EmptyOffersContainer>
    )
  }

  const sortedOffers = [...offersQuery.data.data].sort((a, b) => {
    if (a.isVip && !b.isVip) return -1
    if (!a.isVip && b.isVip) return 1

    if (a.isVip === b.isVip) {
      const dateA = new Date(a.pubStartDate)
      const dateB = new Date(b.pubStartDate)
      return dateB - dateA
    }

    return 0
  })

  return (
    <>
      <OffersGrid>
        {sortedOffers.map(offer => (
          <OfferCard key={offer.id} offer={offer} onClick={handleOfferClick} />
        ))}
      </OffersGrid>

      {selectedOfferId && (
        <DetailOverlay onClick={handleCloseDetail}>
          {offerQuery.isLoading ? (
            <LoaderContainer onClick={e => e.stopPropagation()}>
              <Loader />
            </LoaderContainer>
          ) : (
            <OfferDetail
              offer={offerQuery.data}
              onClose={handleCloseDetail}
              isLoading={offerQuery.isLoading}
            />
          )}
        </DetailOverlay>
      )}
    </>
  )
}

const EmptyOffersContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.black.hex()};
  text-align: center;
  padding: 2rem;
`

const OffersGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  ${breakpoint.s`
    grid-template-columns: repeat(2, 1fr);
  `}
`

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.white.hex()};

  &:hover {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
    border-left: 3px solid ${({ theme }) => theme.colors.deYork.hex()};
  }
`

const CardContent = styled.div`
  padding: 1.5rem;
  background-color: white;
  position: relative;
  box-shadow: 0 -4px 8px -4px rgba(0, 0, 0, 0.15);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const CardTitle = styled.h3`
  margin: 0;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`

const CardDescription = styled.p`
  margin: 0;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.black};
  opacity: 0.6;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DiscountTag = styled.span`
  position: absolute;
  top: -15px;
  right: 10px;
  background-color: ${({ theme }) => theme.colors.apple.hex()};
  color: white;
  padding: 0.3rem 0.6rem;
  border-radius: 0;
  font-weight: bold;
  font-size: 0.9rem;
`

const DetailOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

export default OffersList
